import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { deleteJobAdmin } from '../services/jobService';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

const SuperAdminJobs = () => {
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [appliedUsers, setAppliedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    jobType: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    avgApplicationsPerJob: 0
  });
  const [deletingJob, setDeletingJob] = useState(null);

  useEffect(() => {
    // Check if company filter was passed from navigation
    if (location.state?.companyFilter) {
      setCompanyFilter(location.state.companyFilter);
      setCompanyName(location.state.companyName || '');
      console.log('Company filter applied:', location.state.companyFilter, location.state.companyName);
    }

    // Check if status filter was passed from navigation (e.g., from dashboard Active Jobs card)
    if (location.state?.filterStatus) {
      setFilters(prev => ({
        ...prev,
        status: location.state.filterStatus
      }));
      console.log('Status filter applied:', location.state.filterStatus);
    }

    fetchJobs();
  }, [location.state]);

  useEffect(() => {
    filterJobs();
  }, [jobs, filters, companyFilter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const jobsData = [];

      for (const jobDoc of jobsSnapshot.docs) {
        const jobData = { id: jobDoc.id, ...jobDoc.data() };

        // Fetch company details
        if (jobData.companyId) {
          try {
            const companyDoc = await getDoc(doc(db, 'companies', jobData.companyId));
            if (companyDoc.exists()) {
              jobData.companyDetails = companyDoc.data();
            }
          } catch (error) {
            console.error('Error fetching company details:', error);
          }
        }

        // Get application count
        try {
          const applicationsSnapshot = await getDocs(collection(db, 'applications'));
          const applicationCount = applicationsSnapshot.docs.filter(
            appDoc => appDoc.data().jobId === jobData.id
          ).length;
          jobData.applicationCount = applicationCount;
        } catch (error) {
          console.error('Error fetching applications:', error);
          jobData.applicationCount = 0;
        }

        jobsData.push(jobData);
      }

      setJobs(jobsData);

      // Calculate stats
      const totalJobs = jobsData.length;
      const activeJobs = jobsData.filter(job => job.status === 'active').length;
      const totalApplications = jobsData.reduce((sum, job) => sum + (job.applicationCount || 0), 0);
      const avgApplicationsPerJob = totalJobs > 0 ? (totalApplications / totalJobs).toFixed(1) : 0;

      setStats({
        totalJobs,
        activeJobs,
        totalApplications,
        avgApplicationsPerJob
      });

    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    // Filter by company if set
    if (companyFilter) {
      filtered = filtered.filter(job => job.userId === companyFilter || job.companyId === companyFilter);
      console.log('Filtered by company:', companyFilter, 'Results:', filtered.length);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(job => job.status === filters.status);
    }

    if (filters.jobType !== 'all') {
      filtered = filtered.filter(job => job.jobType === filters.jobType);
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(job => {
        const locationText = typeof job.location === 'object'
          ? `${job.location.city || ''} ${job.location.state || ''} ${job.location.country || ''}`.toLowerCase()
          : (job.location || '').toLowerCase();

        return job.title?.toLowerCase().includes(searchTerm) ||
          job.companyDetails?.name?.toLowerCase().includes(searchTerm) ||
          locationText.includes(searchTerm);
      });
    }

    // Sort by creation date (latest first)
    filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA; // Latest first
    });

    setFilteredJobs(filtered);
  };

  const fetchAppliedUsers = async (jobId) => {
    setLoadingUsers(true);
    console.log('Fetching applied users for job:', jobId);
    try {
      const applicationsSnapshot = await getDocs(collection(db, 'applications'));
      console.log('Total applications in DB:', applicationsSnapshot.docs.length);

      const jobApplications = applicationsSnapshot.docs.filter(
        doc => doc.data().jobId === jobId
      );
      console.log('Applications for this job:', jobApplications.length);

      // Map applications to user data - using embedded data from application document
      const usersData = jobApplications.map(appDoc => {
        const appData = appDoc.data();
        console.log('Processing application:', appDoc.id, appData);

        // Use the embedded applicant data from the application document
        // This matches the structure from applicationService.js (lines 73-104)
        return {
          id: appDoc.id,
          applicationId: appDoc.id,
          userId: appData.applicantId || appData.userId,
          appliedAt: appData.appliedAt,
          status: appData.status || 'pending',
          coverLetter: appData.coverLetter || '',
          resume: appData.resumeURL || appData.resume || '',
          // Applicant details (embedded in application)
          name: appData.applicantName || 'Unknown',
          email: appData.applicantEmail || 'No email',
          phone: appData.applicantPhone || 'No phone',
          experience: appData.applicantProfile?.experience || appData.experience || 'Not specified',
          skills: appData.applicantProfile?.skills || appData.skills || [],
          location: appData.applicantLocation || appData.location || 'Not specified',
          profilePicture: appData.applicantProfile?.profilePicture || null,
          // Additional profile data
          professionalSummary: appData.applicantProfile?.professionalSummary || '',
          education: appData.applicantProfile?.education || [],
          expectedSalary: appData.applicantProfile?.expectedSalary || null,
          noticePeriod: appData.applicantProfile?.noticePeriod || ''
        };
      });

      // Sort by application date (latest first)
      usersData.sort((a, b) => {
        const dateA = a.appliedAt?.toDate ? a.appliedAt.toDate() : new Date(a.appliedAt);
        const dateB = b.appliedAt?.toDate ? b.appliedAt.toDate() : new Date(b.appliedAt);
        return dateB - dateA;
      });

      console.log('Final users data:', usersData);
      setAppliedUsers(usersData);
    } catch (error) {
      console.error('Error fetching applied users:', error);
      setAppliedUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobModal(true);
    fetchAppliedUsers(job.id);
  };

  const exportAppliedUsers = () => {
    if (appliedUsers.length === 0) return;

    const csvContent = [
      ['Name', 'Email', 'Phone', 'Experience', 'Skills', 'Location', 'Applied Date', 'Status', 'Cover Letter'],
      ...appliedUsers.map(user => [
        user.name,
        user.email,
        user.phone,
        user.experience,
        Array.isArray(user.skills) ? user.skills.join(', ') : user.skills,
        user.location,
        formatDate(user.appliedAt),
        user.status,
        user.coverLetter || 'No cover letter'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedJob?.title || 'job'}_applications.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportJobs = () => {
    if (filteredJobs.length === 0) return;

    const headers = [
      'Job Title',
      'Company',
      'Status',
      'Job Type',
      'Experience',
      'Location',
      'Salary',
      'Applications',
      'Posted Date',
      'Deadline'
    ];

    const csvContent = [
      headers,
      ...filteredJobs.map(job => [
        job.title,
        job.companyDetails?.name || job.company || 'Unknown',
        job.status,
        job.jobType,
        job.experienceLevel,
        // Location
        job.location?.city || job.location?.state || job.location?.country
          ? `${job.location.city || ''} ${job.location.state || ''} ${job.location.country || ''}`.trim()
          : (typeof job.location === 'string' ? job.location : 'Not specified'),
        // Salary
        formatSalary(job.salary),
        job.applicationCount || 0,
        formatDate(job.createdAt),
        formatDate(job.applicationDeadline)
      ].map(field => {
        // Handle null/undefined and escape quotes
        const stringField = (field || '').toString().replace(/"/g, '""');
        return `"${stringField}"`;
      }))
    ].map(row => row.join(',')).join('\n');

    // Add BOM for Excel unicode support
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jobs_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const formatSalary = (salaryObj) => {
    if (!salaryObj) return 'Not specified';

    // Handle new salary object structure
    if (typeof salaryObj === 'object' && salaryObj.min && salaryObj.max) {
      const min = parseInt(salaryObj.min);
      const max = parseInt(salaryObj.max);
      const currency = salaryObj.currency === 'INR' ? '₹' : '$';
      const period = salaryObj.period || 'month';
      const negotiable = salaryObj.negotiable ? ' (Negotiable)' : '';

      return `${currency}${min.toLocaleString()} - ${currency}${max.toLocaleString()}/${period}${negotiable}`;
    }

    // Handle legacy format
    if (typeof salaryObj === 'number') return `₹${salaryObj.toLocaleString()}`;

    return 'Not specified';
  };

  const handleDeleteJob = async (jobId, jobTitle) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${jobTitle}"?\n\nThis will permanently delete:\n- Job posting from database\n- All job data\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingJob(jobId);

    try {
      const result = await deleteJobAdmin(jobId);

      if (result.success) {
        // Remove job from local state
        setJobs(prev => prev.filter(j => j.id !== jobId));
        setFilteredJobs(prev => prev.filter(j => j.id !== jobId));

        // Close modal if this job was selected
        if (selectedJob?.id === jobId) {
          setShowJobModal(false);
          setSelectedJob(null);
        }

        // Update stats
        setStats(prev => ({
          ...prev,
          totalJobs: prev.totalJobs - 1
        }));

        alert('Job deleted successfully!');
      } else {
        alert(`Failed to delete job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('An error occurred while deleting the job. Please try again.');
    } finally {
      setDeletingJob(null);
    }
  };

  const handleDeleteExpiredJobs = async () => {
    const expiredJobsList = jobs.filter(job => job.status === 'expired');

    if (expiredJobsList.length === 0) {
      alert('No expired jobs found.');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete all ${expiredJobsList.length} expired jobs?\n\nThis will permanently delete these jobs from the database.`
    );

    if (!confirmDelete) return;

    setLoading(true);
    try {
      let successCount = 0;
      let failCount = 0;

      const deletePromises = expiredJobsList.map(async (job) => {
        try {
          const result = await deleteJobAdmin(job.id);
          if (result.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          console.error(`Error deleting job ${job.id}:`, err);
          failCount++;
        }
      });

      await Promise.all(deletePromises);
      await fetchJobs();

      if (failCount === 0) {
        alert(`Successfully deleted all ${successCount} expired jobs.`);
      } else {
        alert(`Deleted ${successCount} expired jobs. Failed to delete ${failCount} jobs.`);
      }
    } catch (error) {
      console.error('Error deleting expired jobs:', error);
      alert('An error occurred while deleting expired jobs.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {companyFilter && companyName ? `Jobs by ${companyName}` : 'Jobs Management'}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              {companyFilter && companyName
                ? `Viewing all jobs posted by ${companyName}`
                : 'Monitor and manage all job postings on the platform'
              }
            </p>
          </div>
          {companyFilter && (
            <button
              onClick={() => {
                setCompanyFilter(null);
                setCompanyName('');
                window.history.replaceState({}, document.title);
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium flex items-center gap-2 self-start sm:self-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear Company Filter
            </button>
          )}
        </div>
        {companyFilter && companyName && (
          <div className="mt-3 inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Filtered by Company: {companyName}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Applications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.avgApplicationsPerJob}</p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Jobs</label>
            <input
              type="text"
              placeholder="Search by title, company, location..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="paused">Paused</option>
              <option value="closed">Closed</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
            <select
              value={filters.jobType}
              onChange={(e) => setFilters(prev => ({ ...prev, jobType: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="full-time">Full Time</option>
              <option value="part-time">Part Time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: 'all', jobType: 'all', search: '' })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            All Jobs ({filteredJobs.length})
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={exportJobs}
              disabled={filteredJobs.length === 0}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${filteredJobs.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
                }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export Jobs
            </button>
            <button
              onClick={handleDeleteExpiredJobs}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Expires Jobs
            </button>
            <div className="text-sm text-gray-500">
              Sorted by latest first
            </div>
          </div>
        </div>

        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                onClick={() => handleJobClick(job)}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl cursor-pointer transition-all duration-300 hover:border-blue-300 transform hover:-translate-y-1"
              >
                {/* Job Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors">
                        {job.title}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${job.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : job.status === 'expired'
                          ? 'bg-red-100 text-red-700'
                          : job.status === 'closed'
                            ? 'bg-gray-100 text-gray-700'
                            : job.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-700'
                              : job.status === 'paused'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-blue-100 text-blue-700'
                        }`}>
                        {job.status?.toUpperCase()}
                      </span>
                    </div>

                    {/* Company and Date */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-gray-600 mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="font-medium">{job.companyDetails?.name || job.company || 'Unknown Company'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                        </svg>
                        <span className="text-sm">Posted: {formatDate(job.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Applications Count */}
                  <div className="self-start sm:self-auto">
                    <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                      <div className="flex items-center gap-2 text-blue-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-bold text-lg">{job.applicationCount || 0}</span>
                      </div>
                      <p className="text-xs text-blue-600 font-medium">Applications</p>
                    </div>
                  </div>
                </div>

                {/* Job Details Grid */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                        {job.location?.city || job.location?.state || job.location?.country
                          ? `${job.location.city || ''}, ${job.location.state || ''}, ${job.location.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                          : typeof job.location === 'string' ? job.location : 'Not specified'
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Salary</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{formatSalary(job.salary)}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 capitalize break-words">{job.jobType?.replace('-', ' ') || 'Not specified'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Experience</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 capitalize break-words">{job.experienceLevel || 'Not specified'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-pink-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M5 7v12a2 2 0 002 2h10a2 2 0 002-2V7M8 7V4a2 2 0 012-2h4a2 2 0 012 2v3" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 12h4M10 16h4" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Vacancies</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900">{job.vacancies || 1}</p>
                    </div>
                  </div>
                </div>

                {/* Job Tags */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {job.urgent && (
                    <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                      🔥 URGENT
                    </span>
                  )}
                  {job.featured && (
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                      ⭐ FEATURED
                    </span>
                  )}
                  {job.department && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                      {job.department.toUpperCase()}
                    </span>
                  )}
                  {job.location?.hybrid && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      🏢 HYBRID
                    </span>
                  )}
                  {job.location?.remote && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                      🏠 REMOTE
                    </span>
                  )}
                </div>

                {/* Job Description */}
                <p className="text-gray-600 text-xs sm:text-sm mb-4 leading-relaxed break-words">
                  {job.description?.substring(0, 200)}...
                </p>

                {/* Job Footer */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4 border-t border-gray-100">
                  <div className="text-xs sm:text-sm text-gray-500">
                    Deadline: {formatDate(job.applicationDeadline) || 'Not specified'}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteJob(job.id, job.title);
                      }}
                      disabled={deletingJob === job.id}
                      className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 ${deletingJob === job.id
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                      title="Delete job"
                    >
                      {deletingJob === job.id ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          <span className="text-xs">Deleting...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="text-xs">Delete</span>
                        </>
                      )}
                    </button>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs sm:text-sm text-blue-600 font-medium">View Details</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">💼</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
            <p className="text-gray-500">No jobs match your current filters.</p>
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {showJobModal && selectedJob && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100">
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 sm:p-6 lg:p-8">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4 sm:gap-6">
                  {/* Job Icon */}
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-2xl sm:text-3xl border-4 border-white/20 shadow-lg">
                    💼
                  </div>

                  <div className="text-white">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">{selectedJob.title}</h2>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border ${selectedJob.status === 'active'
                        ? 'bg-green-500/20 text-green-100 border-green-400/30'
                        : selectedJob.status === 'expired'
                          ? 'bg-red-500/20 text-red-100 border-red-400/30'
                          : selectedJob.status === 'closed'
                            ? 'bg-gray-500/20 text-gray-100 border-gray-400/30'
                            : selectedJob.status === 'draft'
                              ? 'bg-yellow-500/20 text-yellow-100 border-yellow-400/30'
                              : selectedJob.status === 'paused'
                                ? 'bg-orange-500/20 text-orange-100 border-orange-400/30'
                                : 'bg-blue-500/20 text-blue-100 border-blue-400/30'
                        }`}>
                        {selectedJob.status === 'active' ? '✓ ACTIVE'
                          : selectedJob.status === 'expired' ? '⏰ EXPIRED'
                            : selectedJob.status === 'closed' ? '✗ CLOSED'
                              : selectedJob.status === 'draft' ? '📝 DRAFT'
                                : selectedJob.status === 'paused' ? '⏸️ PAUSED'
                                  : '⏳ ' + selectedJob.status?.toUpperCase()}
                      </span>
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
                        {selectedJob.companyDetails?.name || selectedJob.company || 'Unknown Company'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-white/80 text-sm sm:text-base">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm">
                          {selectedJob.location?.city || selectedJob.location?.state || selectedJob.location?.country
                            ? `${selectedJob.location.city || ''}, ${selectedJob.location.state || ''}, ${selectedJob.location.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : typeof selectedJob.location === 'string' ? selectedJob.location : 'Location not specified'
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        <span className="text-sm font-semibold">{formatSalary(selectedJob.salary)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-sm font-semibold">{selectedJob.applicationCount || 0} Applications</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowJobModal(false)}
                  className="absolute top-2 right-2 sm:relative sm:top-auto sm:right-auto p-2 sm:p-3 hover:bg-white/10 rounded-xl transition-colors text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                <div className="space-y-4 sm:space-y-6">
                  {/* Job Description */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Job Description</h3>
                    </div>
                    <div className="prose max-w-none text-gray-700 leading-relaxed">
                      {typeof selectedJob.description === 'string'
                        ? selectedJob.description.split('\n').map((paragraph, index) => (
                          <p key={index} className="mb-4 text-gray-600">{paragraph}</p>
                        ))
                        : <p className="mb-4 text-gray-600">{selectedJob.description || 'No description available'}</p>
                      }
                    </div>
                  </div>

                  {/* Requirements */}
                  {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Requirements</h3>
                      </div>
                      <div className="space-y-3">
                        {Array.isArray(selectedJob.requirements)
                          ? selectedJob.requirements.map((req, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                              <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                              <span className="text-gray-700">{req}</span>
                            </div>
                          ))
                          : <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                            <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                            <span className="text-gray-700">{selectedJob.requirements}</span>
                          </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Responsibilities */}
                  {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-5a2 2 0 00-2-2h-2m-1 4h2a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2m-1 4h.01M9 16h.01" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Responsibilities</h3>
                      </div>
                      <div className="space-y-3">
                        {Array.isArray(selectedJob.responsibilities)
                          ? selectedJob.responsibilities.map((resp, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                              <span className="text-gray-700">{resp}</span>
                            </div>
                          ))
                          : <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            <span className="text-gray-700">{selectedJob.responsibilities}</span>
                          </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    {selectedJob.requiredSkills && selectedJob.requiredSkills.length > 0 && (
                      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Required Skills</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedJob.requiredSkills.map((skill, index) => (
                            <span key={index} className="px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedJob.preferredSkills && selectedJob.preferredSkills.length > 0 && (
                      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Preferred Skills</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedJob.preferredSkills.map((skill, index) => (
                            <span key={index} className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Application Instructions */}
                  {selectedJob.applicationInstructions && selectedJob.applicationInstructions.trim() && (
                    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Application Instructions</h3>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-gray-700">{selectedJob.applicationInstructions}</p>
                      </div>
                    </div>
                  )}

                  {selectedJob.benefits && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Benefits</h3>
                      <div className="prose max-w-none text-gray-700">
                        {typeof selectedJob.benefits === 'string'
                          ? selectedJob.benefits.split('\n').map((benefit, index) => (
                            <p key={index} className="mb-2">• {benefit}</p>
                          ))
                          : Array.isArray(selectedJob.benefits)
                            ? selectedJob.benefits.map((benefit, index) => (
                              <p key={index} className="mb-2">• {benefit}</p>
                            ))
                            : <p className="mb-2">• {selectedJob.benefits}</p>
                        }
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar - Job Details */}
                <div>
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Job Details</h3>
                    </div>

                    <div className="space-y-4">
                      {/* Company */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Company</p>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedJob.companyDetails?.name || selectedJob.company || 'Unknown'}</p>
                      </div>

                      {/* Location */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Location</p>
                        </div>
                        <p className="text-gray-900 font-medium">
                          {selectedJob.location?.city || selectedJob.location?.state || selectedJob.location?.country
                            ? `${selectedJob.location.city || ''}, ${selectedJob.location.state || ''}, ${selectedJob.location.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : typeof selectedJob.location === 'string' ? selectedJob.location : 'Not specified'
                          }
                        </p>
                      </div>

                      {/* Salary */}
                      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          <p className="text-sm font-semibold text-green-700">Salary Range</p>
                        </div>
                        <p className="text-lg font-bold text-green-900">{formatSalary(selectedJob.salary)}</p>
                      </div>

                      {/* Job Type & Work Mode */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Job Type</p>
                          </div>
                          <p className="text-gray-900 font-medium capitalize">{selectedJob.jobType?.replace('-', ' ') || 'Not specified'}</p>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Work Mode</p>
                          </div>
                          <p className="text-gray-900 font-medium capitalize">{selectedJob.workMode || 'Not specified'}</p>
                        </div>
                      </div>

                      {/* Experience & Department */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Experience</p>
                          </div>
                          <p className="text-gray-900 font-medium capitalize">{selectedJob.experienceLevel || 'Not specified'}</p>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Department</p>
                          </div>
                          <p className="text-gray-900 font-medium capitalize">{selectedJob.department || 'Not specified'}</p>
                        </div>
                      </div>

                      {/* Applications Count */}
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className="text-sm font-semibold text-blue-700">Applications Received</p>
                        </div>
                        <p className="text-2xl font-bold text-blue-900">{selectedJob.applicationCount || 0}</p>
                      </div>

                      {/* Job Flags */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Job Tags</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedJob.urgent && (
                            <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                              🔥 URGENT
                            </span>
                          )}
                          {selectedJob.featured && (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                              ⭐ FEATURED
                            </span>
                          )}
                          {selectedJob.location?.hybrid && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                              🏢 HYBRID
                            </span>
                          )}
                          {selectedJob.location?.remote && (
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                              🏠 REMOTE
                            </span>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedJob.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : selectedJob.status === 'closed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                            }`}>
                            {selectedJob.status?.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Posted</p>
                          </div>
                          <p className="text-gray-900 font-medium">{formatDate(selectedJob.createdAt)}</p>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Deadline</p>
                          </div>
                          <p className="text-gray-900 font-medium">{formatDate(selectedJob.applicationDeadline) || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Applied Users Section */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 mt-4 sm:mt-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900">Applied Users ({appliedUsers.length})</h3>
                      </div>

                      {appliedUsers.length > 0 && (
                        <button
                          onClick={exportAppliedUsers}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export CSV
                        </button>
                      )}
                    </div>

                    {loadingUsers ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        <span className="ml-3 text-gray-600">Loading applied users...</span>
                      </div>
                    ) : appliedUsers.length > 0 ? (
                      <div className="space-y-3 sm:space-y-4 max-h-96 overflow-y-auto">
                        {appliedUsers.map((user) => (
                          <div key={user.id} className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                              {/* User Avatar */}
                              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg self-center sm:self-start">
                                {user.name.charAt(0).toUpperCase()}
                              </div>

                              {/* User Details */}
                              <div className="flex-1 w-full">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                  <h4 className="text-base sm:text-lg font-semibold text-gray-900">{user.name}</h4>
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user.status === 'accepted'
                                    ? 'bg-green-100 text-green-700'
                                    : user.status === 'rejected'
                                      ? 'bg-red-100 text-red-700'
                                      : user.status === 'interviewed'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    {user.status.toUpperCase()}
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 gap-2 sm:gap-3 mb-3">
                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 break-all">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <span className="break-all">{user.email}</span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span>{user.phone}</span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span>{user.location}</span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                                    </svg>
                                    <span>Applied: {formatDate(user.appliedAt)}</span>
                                  </div>
                                </div>

                                {/* Experience */}
                                <div className="mb-3">
                                  <span className="text-sm font-medium text-gray-700">Experience: </span>
                                  <span className="text-sm text-gray-600">
                                    {Array.isArray(user.experience) && user.experience.length > 0
                                      ? user.experience.map((exp, idx) =>
                                        `${exp.jobTitle || 'N/A'} at ${exp.company || 'N/A'}`
                                      ).join(', ')
                                      : typeof user.experience === 'string'
                                        ? user.experience
                                        : 'Not specified'
                                    }
                                  </span>
                                </div>

                                {/* Skills */}
                                {user.skills && user.skills.length > 0 && (
                                  <div className="mb-3">
                                    <span className="text-sm font-medium text-gray-700 block mb-2">Skills:</span>
                                    <div className="flex flex-wrap gap-1">
                                      {(Array.isArray(user.skills) ? user.skills : user.skills.split(',')).slice(0, 5).map((skill, index) => (
                                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                          {skill.trim()}
                                        </span>
                                      ))}
                                      {(Array.isArray(user.skills) ? user.skills : user.skills.split(',')).length > 5 && (
                                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                          +{(Array.isArray(user.skills) ? user.skills : user.skills.split(',')).length - 5} more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Cover Letter */}
                                {user.coverLetter && (
                                  <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                                    <span className="text-sm font-medium text-gray-700 block mb-1">Cover Letter:</span>
                                    <p className="text-sm text-gray-600 line-clamp-3">{user.coverLetter}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 text-4xl mb-3">👥</div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Applications Yet</h4>
                        <p className="text-gray-500">No users have applied to this job yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminJobs;
